import { withAnalyticsTracking } from '@/utilities/analytics/apiAnalytics'
import { errorAnalyticsPayload } from '@/utilities/analytics/errorAnalytics'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import {
  syncPartnerIdsAction,
  type PartnerIdsSyncFailureReason,
} from '@/services/payload/payloadServerRepository'
import type { SessionJwtFailureReason } from '@/utilities/stytch/constants'

export type PartnerSyncContext = 'existing-user-authentication' | 'new-user-registration'

type SyncPartnerIds = {
  context: PartnerSyncContext
  analyticsProperties?: {
    kitId?: string
  }
}

export function createPartnerSyncEventId(): string {
  const cryptoApi = globalThis.crypto
  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID()
  }

  if (typeof cryptoApi?.getRandomValues !== 'function') {
    throw new TypeError('Secure random number generation is not available')
  }

  const bytes = new Uint8Array(16)
  cryptoApi.getRandomValues(bytes)
  let randomHex = ''
  for (const byte of bytes) {
    randomHex += byte.toString(16).padStart(2, '0')
  }
  return `${Date.now()}-${randomHex}`
}

export async function syncTenantPartnerIds({ context, analyticsProperties }: SyncPartnerIds) {
  const syncEventId = createPartnerSyncEventId()
  const additionalProperties = {
    context,
    syncEventId,
    ...analyticsProperties,
  }

  let sessionJwtReason: SessionJwtFailureReason | undefined
  let failureReason: PartnerIdsSyncFailureReason | undefined

  try {
    const result = await withAnalyticsTracking(
      async () => {
        const actionResult = await syncPartnerIdsAction({
          syncEventId,
          context,
        })
        // Return session JWT failures so the reason survives Next.js production
        // transport. Throw other expected failures here so API analytics records them.
        if (!actionResult.success) {
          failureReason = actionResult.reason
          if (actionResult.sessionJwtReason) {
            return actionResult
          }
          throw new Error(actionResult.message)
        }
        return actionResult
      },
      {
        eventName: 'tenant:sync_partner_ids_api',
        additionalProperties,
      },
    )

    if (!result.success) {
      sessionJwtReason = result.sessionJwtReason
      failureReason = result.reason
      throw new Error('Missing or invalid session')
    }

    if (result.skipped) {
      if (result.skippedReason === 'impersonated') {
        trackEvent('tenant:sync_partner_ids_skip_impersonated', {
          ...additionalProperties,
          gate: 'partner_id_sync',
          source: 'syncPartnerIdsAction',
        })
        return
      }

      trackEvent('tenant:sync_partner_ids_skip_already_exists', additionalProperties)
      return
    }

    trackEvent('tenant:sync_partner_ids_success', additionalProperties)
  } catch (error) {
    trackEvent('tenant:sync_partner_id_fail', {
      ...additionalProperties,
      ...errorAnalyticsPayload(error),
      ...(failureReason ? { reason: failureReason } : {}),
      ...(sessionJwtReason ? { sessionJwtReason } : {}),
    })
  }
}
